import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './LoginPage.css'; // Reusing Login styles for consistent look
import { updatePassword, getCurrentSession, subscribeToAuthStateChanges } from '../services/authService';

function UpdatePassword() { // Renamed from ResetPasswordRequest to match filename/purpose
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [sessionCheckComplete, setSessionCheckComplete] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let mounted = true;
        let sessionFound = false;

        // 1. Set up the listener (Crucial for PASSWORD_RECOVERY flow)
        const subscription = subscribeToAuthStateChanges(async (event, session) => {
            if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
                if (mounted) {
                    sessionFound = true;
                    setSessionCheckComplete(true);
                    setError('');
                }
            }
        });

        // 2. Check current session (in case event fired before mount)
        getCurrentSession().then(session => {
            console.log('Session check:', session);
            if (session && mounted) {
                sessionFound = true;
                setSessionCheckComplete(true);
            }
        }).catch(err => {
            console.error('Session check failed:', err);
        });

        // 3. Fallback timeout: If no session/recovery detected after 2 seconds, assume link is invalid
        const timeoutId = setTimeout(() => {
            if (mounted && !sessionFound) {
                // Double check one last time
                getCurrentSession().then(session => {
                    if (!session && mounted) {
                        setError('Invalid or expired password reset link. Please try initiating a reset again.');
                        setSessionCheckComplete(true); // Show the error
                    }
                });
            }
        }, 3000);

        return () => {
            mounted = false;
            if (subscription && typeof subscription.unsubscribe === 'function') {
                subscription.unsubscribe();
            }
            clearTimeout(timeoutId);
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        try {
            setLoading(true);
            const { error } = await updatePassword(password);

            if (error) {
                throw error;
            }

            setMessage('Password updated successfully! Redirecting to login...');
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to update password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!sessionCheckComplete) {
        return <div className="login-page-container"><p style={{ color: 'white' }}>Validating reset link...</p></div>;
    }

    return (
        <div className="login-page-container">
            <div className="login-header">
                <div className="login-logo">Task Square (Beta)</div>
            </div>

            <div className="login-form-card">
                <h2>Set New Password</h2>
                <p className="login-subtitle">Enter your new password below</p>

                {message && <p className="success-message">{message}</p>}
                {error && <p className="error-message">{error}</p>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="password">New Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading || !!error}
                            placeholder="••••••••"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            disabled={loading || !!error}
                            placeholder="••••••••"
                        />
                    </div>

                    <button type="submit" className="login-button" disabled={loading || !!error}>
                        {loading ? 'Updating...' : 'Update Password'}
                    </button>
                </form>

                <div className="login-footer">
                    <Link to="/login">Cancel</Link>
                </div>
            </div>
        </div>
    );
}

export default UpdatePassword;
